/**
 * Where the magic link's e-mail address is kept between sending the link and opening it.
 *
 * WHY THE APP HAS TO STORE IT AT ALL. Finishing an e-mail-link sign-in needs the address the link
 * was sent to, and the link itself does not carry it — deliberately, since a link that carried the
 * address would let anyone who saw the URL sign in as that person from any device. So the SPA
 * remembers it locally and asks for it again when it cannot
 * (docs/design/research/13-web-app-stack-research.md).
 *
 * WHICH MAKES "OPENED ON A DIFFERENT DEVICE" A NORMAL CASE, NOT AN ERROR. Someone asks for the link
 * on a laptop and opens it on a phone, or in a different browser, or after clearing site data. The
 * screen asks for the address again; it does not tell them the link is broken.
 *
 * `localStorage` CAN THROW, not just come back empty — private windows and blocked site data both
 * do it — so every access is wrapped. A storage failure must degrade to "ask for the address
 * again", never to a white page.
 */

const STORAGE_KEY = 'moonx.authors.magic-link-email';

export function rememberMagicLinkAddress(email: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, email);
  } catch {
    // Nothing to do and nothing to report: the handler will ask for the address again, which is a
    // supported path rather than a failure.
  }
}

export function recallMagicLinkAddress(): string | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored !== null && stored.length > 0 ? stored : null;
  } catch {
    return null;
  }
}

export function forgetMagicLinkAddress(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // As above.
  }
}

/**
 * The lightest check that catches a typo and refuses nothing real.
 *
 * Deliberately NOT a full address grammar: the platform sends the mail and the mailbox is the only
 * thing that can actually confirm an address. A strict client-side pattern here would reject valid
 * addresses and teach nobody anything.
 */
export function looksLikeEmailAddress(value: string): boolean {
  const trimmed = value.trim();
  const at = trimmed.indexOf('@');
  return at > 0 && at < trimmed.length - 1 && !trimmed.includes(' ');
}
